import React, { useState, useEffect } from 'react';
import axios from 'axios';
import '../styles/UserManagement.css';

// Role hierarchy: owner > admin > supervisor > staff.
const ROLE_LABELS = { owner: 'Owner', admin: 'Admin', supervisor: 'Supervisor', staff: 'Staff' };

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editUser, setEditUser] = useState(null);
  
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    firstName: '',
    lastName: '',
    email: '',
    role: 'staff',
    betaTester: false
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/admin/users`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setUsers(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching users:', error);
      alert('Failed to load users');
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const token = localStorage.getItem('token');

      if (editUser) {
        // Update existing user
        await axios.put(
          `${process.env.REACT_APP_API_URL}/admin/users/${editUser.id}`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        alert('User updated successfully');
      } else {
        // Create new user
        await axios.post(
          `${process.env.REACT_APP_API_URL}/admin/users`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        alert('User created successfully');
      }

      setShowModal(false);
      setEditUser(null);
      setFormData({
        username: '',
        password: '',
        firstName: '',
        lastName: '',
        email: '',
        role: 'staff',
        betaTester: false
      });
      fetchUsers();
    } catch (error) {
      console.error('Error saving user:', error);
      alert(error.response?.data?.error || 'Failed to save user');
    }
  };

  const handleEdit = (user) => {
    setEditUser(user);
    setFormData({
      username: user.username,
      password: '', // Don't populate password
      firstName: user.first_name,
      lastName: user.last_name,
      email: user.email,
      role: user.role,
      betaTester: !!user.beta_tester
    });
    setShowModal(true);
  };

  const handleToggleBeta = async (user) => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.put(
        `${process.env.REACT_APP_API_URL}/admin/users/${user.id}/beta-tester`,
        { beta_tester: !user.beta_tester },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setUsers(prev => prev.map(u => (u.id === user.id ? { ...u, beta_tester: res.data.beta_tester } : u)));
    } catch (error) {
      console.error('Error toggling beta access:', error);
      alert('Failed to update beta access');
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/admin/users/${userId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert('User deleted successfully');
      fetchUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
      alert('Failed to delete user');
    }
  };

  const handleResetPassword = async (userId, username, fullName) => {
    if (!window.confirm(`Reset password for ${fullName}?\n\nThis will generate a new temporary password.`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/admin/reset-user-password/${userId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const tempPassword = response.data.tempPassword;
      
      // Copy to clipboard
      navigator.clipboard.writeText(`Username: ${username}\nTemp Password: ${tempPassword}`);
      
      alert(`Password Reset Complete!\n\nUsername: ${username}\nTemp Password: ${tempPassword}\n\n✓ Credentials copied to clipboard!`);
    } catch (error) {
      console.error('Error resetting password:', error);
      alert('Failed to reset password');
    }
  };

  const handleAddNew = () => {
    setEditUser(null);
    setFormData({
      username: '',
      password: '',
      firstName: '',
      lastName: '',
      email: '',
      role: 'staff',
      betaTester: false
    });
    setShowModal(true);
  };

  if (loading) {
    return <div className="loading">Loading users...</div>;
  }

  return (
    <div className="user-management">
      <div className="users-header">
        <div>
          <h2>User Management</h2>
          <p>Manage user accounts and permissions</p>
        </div>
        <button onClick={handleAddNew} className="btn-add-user">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
            <circle cx="8.5" cy="7" r="4"/>
            <line x1="20" y1="8" x2="20" y2="14"/>
            <line x1="23" y1="11" x2="17" y2="11"/>
          </svg>
          Add User
        </button>
      </div>

      <div className="users-table">
        <table>
          <thead>
            <tr>
              <th>Name</th>
              <th>Username</th>
              <th>Email</th>
              <th>Role</th>
              <th>Status</th>
              <th>Beta</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map(user => (
              <tr key={user.id}>
                <td>
                  <div className="user-name">
                    <div className="user-avatar">
                      {user.first_name?.[0]}{user.last_name?.[0]}
                    </div>
                    <div>
                      <div className="name">{user.first_name} {user.last_name}</div>
                    </div>
                  </div>
                </td>
                <td>{user.username}</td>
                <td>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span>{user.email}</span>
                    {user.is_google_account && (
                      <span title="Signs in with Google (no password)" style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '10px', fontWeight: 600, color: '#3C4043', background: '#F1F3F4', border: '1px solid #DADCE0', borderRadius: '999px', padding: '2px 8px', whiteSpace: 'nowrap' }}>
                        <svg width="11" height="11" viewBox="0 0 48 48" aria-hidden="true">
                          <path fill="#4285F4" d="M45.12 24.5c0-1.56-.14-3.06-.4-4.5H24v8.51h11.84c-.51 2.75-2.06 5.08-4.39 6.64v5.52h7.11c4.16-3.83 6.56-9.47 6.56-16.17z"/>
                          <path fill="#34A853" d="M24 46c5.94 0 10.92-1.97 14.56-5.33l-7.11-5.52c-1.97 1.32-4.49 2.1-7.45 2.1-5.73 0-10.58-3.87-12.31-9.07H4.34v5.7C7.96 41.07 15.4 46 24 46z"/>
                          <path fill="#FBBC05" d="M11.69 28.18C11.25 26.86 11 25.45 11 24s.25-2.86.69-4.18v-5.7H4.34C2.85 17.09 2 20.45 2 24s.85 6.91 2.34 9.88l7.35-5.7z"/>
                          <path fill="#EA4335" d="M24 10.75c3.23 0 6.13 1.11 8.41 3.29l6.31-6.31C34.91 4.18 29.93 2 24 2 15.4 2 7.96 6.93 4.34 14.12l7.35 5.7c1.73-5.2 6.58-9.07 12.31-9.07z"/>
                        </svg>
                        Google
                      </span>
                    )}
                  </div>
                </td>
                <td>
                  <span className={`role-badge role-${user.role}`}>
                    {ROLE_LABELS[user.role] || 'Staff'}
                  </span>
                </td>
                <td>
                  <span className={`status-badge status-${user.is_active ? 'active' : 'inactive'}`}>
                    {user.is_active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td>
                  {user.role === 'admin' ? (
                    <span title="Admins always have access" style={{ fontSize: '11px', fontWeight: 600, color: '#6B7280' }}>Always</span>
                  ) : (
                    <button
                      onClick={() => handleToggleBeta(user)}
                      title={user.beta_tester ? 'Beta tester — click to remove' : 'Not a beta tester — click to add'}
                      style={{
                        cursor: 'pointer', border: '1px solid', borderRadius: '999px', padding: '3px 12px',
                        fontSize: '11px', fontWeight: 700, whiteSpace: 'nowrap',
                        color: user.beta_tester ? '#0F7B3E' : '#8A8A8E',
                        background: user.beta_tester ? '#E9F8EF' : '#F4F4F6',
                        borderColor: user.beta_tester ? '#B6E6C9' : '#E0E0E4'
                      }}
                    >
                      {user.beta_tester ? '● Tester' : '○ Off'}
                    </button>
                  )}
                </td>
                <td>
                  <div className="action-buttons">
                    <button onClick={() => handleEdit(user)} className="btn-edit" title="Edit">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                      </svg>
                    </button>
                    <button 
                      onClick={() => handleResetPassword(user.id, user.username, `${user.first_name} ${user.last_name}`)} 
                      className="btn-reset" 
                      title="Reset Password"
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                        <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                      </svg>
                    </button>
                    <button onClick={() => handleDelete(user.id)} className="btn-delete" title="Delete">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="3 6 5 6 21 6"/>
                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                      </svg>
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {users.length === 0 && (
          <div className="no-users">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
              <circle cx="9" cy="7" r="4"/>
            </svg>
            <p>No users found</p>
          </div>
        )}
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editUser ? 'Edit User' : 'Add New User'}</h3>
              <button onClick={() => setShowModal(false)} className="btn-close">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"/>
                  <line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              {!editUser && (
                <p style={{ margin: '0 0 16px', padding: '10px 12px', background: '#F0F7FF', border: '1px solid #CFE3FA', borderRadius: '8px', fontSize: '12px', color: '#1E4E79', lineHeight: 1.5 }}>
                  New staff sign in with <strong>Google</strong> using their <strong>@caamuseum.org</strong> email — just set the name, email, and role and leave the password blank. Set a password only for a dev/test login (won't work on the production site, which is Google-only).
                </p>
              )}
              <div className="form-grid">
                <div className="form-group">
                  <label>First Name *</label>
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Last Name *</label>
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Email *</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="form-grid">
                <div className="form-group">
                  <label>{editUser ? 'Username' : 'Username (optional)'}</label>
                  <input
                    type="text"
                    name="username"
                    value={formData.username}
                    onChange={handleInputChange}
                    disabled={editUser !== null}
                    placeholder={editUser ? '' : 'Defaults to the email name'}
                  />
                </div>
                <div className="form-group">
                  <label>{editUser ? 'New Password (leave blank to keep current)' : 'Password (optional — blank = Google sign-in)'}</label>
                  <input
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Role *</label>
                <select name="role" value={formData.role} onChange={handleInputChange}>
                  <option value="owner">Owner</option>
                  <option value="admin">Admin</option>
                  <option value="supervisor">Supervisor</option>
                  <option value="staff">Staff</option>
                </select>
              </div>

              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    name="betaTester"
                    checked={!!formData.betaTester}
                    onChange={(e) => setFormData(prev => ({ ...prev, betaTester: e.target.checked }))}
                    style={{ width: 'auto', margin: 0 }}
                  />
                  <span>Beta tester — allow portal access during the limited beta</span>
                </label>
                <p style={{ margin: '4px 0 0', fontSize: '11px', color: '#8A8A8E' }}>
                  Administrators always have access regardless of this setting.
                </p>
              </div>

              <div className="modal-actions">
                <button type="button" onClick={() => setShowModal(false)} className="btn-cancel">
                  Cancel
                </button>
                <button type="submit" className="btn-submit">
                  {editUser ? 'Update User' : 'Create User'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;